import type { CallCredentials } from '@cyphex/types/calls';
import type { CallKind, DeclineReason, IncomingCall } from './types';

/** Wire shape of the `call.ring` gateway frame on topic `usr:{userId}`. */
export interface IncomingCallDto {
  callId: string;
  kind: CallKind;
  isGroup: boolean;
  otherParticipantCount: number;
  conversationId: string | null;
  ringExpiresAt: string;
  isPriorityCaller: boolean;
  ringtone?: string;
  caller: { id: string; displayName: string; role: string | null; avatarUrl: string | null };
}

/**
 * What answering hands back — the GRANT, not just a room name.
 *
 * A room name on its own is useless: only a token signed by apps/api admits
 * you. The old `{ sfuRoom }` shape is exactly why answering used to navigate to
 * `/meet/c_<callId>` and dead-end on "This meeting is not available" — a call is
 * not a meeting, and its room is reached at `/call/:callId` with these
 * credentials.
 */
export interface AcceptedCall {
  readonly callId: string;
  readonly credentials: CallCredentials;
  readonly kind: CallKind;
  readonly peerName: string;
  readonly peerId: string | null;
  readonly withVideo: boolean;
}

export interface CallsApi {
  /** POST /v1/calls/:callId/answer — returns the media grant for the call room. */
  accept(callId: string, withVideo: boolean): Promise<AcceptedCall>;
  /** POST /api/v1/calls/:callId/decline */
  decline(callId: string, reason: DeclineReason): Promise<void>;
  /** POST /api/v1/calls/:callId/decline with a canned message to the caller. */
  declineWithMessage(callId: string, message: string): Promise<void>;
  /**
   * GET /api/v1/calls/ringing — replay after a reconnect, backed by
   * `ix_call_participants_ringing`.
   */
  listRinging(): Promise<IncomingCall[]>;
  /** WS `usr:{userId}` — ring / cancel frames. Returns an unsubscribe. */
  subscribe(handlers: {
    onRing: (call: IncomingCall) => void;
    onCancel: (callId: string) => void;
  }): () => void;
}

export function incomingCallFromDto(dto: IncomingCallDto, now = Date.now()): IncomingCall {
  return {
    callId: dto.callId,
    kind: dto.kind,
    isGroup: dto.isGroup,
    otherParticipantCount: dto.otherParticipantCount,
    conversationId: dto.conversationId ?? undefined,
    ringingSince: now,
    ringExpiresAt: Date.parse(dto.ringExpiresAt),
    isPriorityCaller: dto.isPriorityCaller,
    ringtone: dto.ringtone ?? 'default',
    caller: {
      id: dto.caller.id,
      displayName: dto.caller.displayName,
      role: dto.caller.role ?? undefined,
      avatarUrl: dto.caller.avatarUrl ?? undefined,
    },
  };
}

/** The canned replies offered under the "Message" affordance. */
export const QUICK_REPLIES: readonly string[] = [
  "I'm in a meeting — can I call you back?",
  'Give me five minutes.',
  "Can't talk right now. Send me a message?",
  "I'll join the call shortly.",
];
